import { formatPrice, currencySymbol } from './currency';

const BASE = '/spring/market';

function redirectToSellerLogin() {
  window.location.href = '/app/market/seller/login';
}

async function request(path, options = {}) {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  });
  if (res.redirected && res.url.includes('login')) { redirectToSellerLogin(); return null; }
  if (res.status === 401 || res.status === 403) { redirectToSellerLogin(); return null; }
  let body;
  try {
    body = await res.json();
  } catch {
    const ct = res.headers.get('content-type') || '';
    if (!res.ok && ct.includes('text/html')) { redirectToSellerLogin(); return null; }
    body = null;
  }
  if (body && body.error === 'Not authenticated') { redirectToSellerLogin(); return null; }
  if (!res.ok || (body && body.error)) {
    const msg =
      (body && (body.message || body.error || JSON.stringify(body))) ||
      `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return body;
}

export function getCategories() {
  return request('/categories');
}

export function getCities() {
  return request('/cities');
}

export function getProducts(params = {}) {
  const qs = new URLSearchParams();
  if (params.city) qs.set('city', params.city);
  if (params.categoryId) qs.set('categoryId', params.categoryId);
  if (params.search) qs.set('search', params.search);
  if (params.sellerId) qs.set('sellerId', params.sellerId);
  if (params.page !== undefined) qs.set('page', params.page);
  if (params.size !== undefined) qs.set('size', params.size);
  const query = qs.toString();
  return request(`/products${query ? `?${query}` : ''}`);
}

export function getSellerByDomain(host) {
  return fetch(`${BASE}/seller-by-domain?host=${encodeURIComponent(host)}`, { credentials: 'include' })
    .then(r => r.ok ? r.json() : null)
    .catch(() => null);
}

export function getProduct(id) {
  return request(`/products/${id}`);
}

export function placeOrder(data) {
  return request('/orders', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function initiateToucanPayment(data) {
  return request('/payment/toucan/initiate', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function trackOrder(ref) {
  return request(`/orders/${encodeURIComponent(ref)}`);
}

export function isEnquiryCategory(catName) {
  const n = (catName || '').toLowerCase();
  return n.includes('service') || n.includes('real estate') || n.includes('realestate');
}

export function isRealEstateCategory(catName) {
  const n = (catName || '').toLowerCase();
  return n.includes('real estate') || n.includes('realestate') || n.includes('property');
}

export function getProductImages(productId) {
  return request(`/products/${productId}/images`);
}

export function addProductImage(productId, url, sortOrder = 0, mediaType = 'image') {
  return request(`/seller/products/${productId}/images`, {
    method: 'POST',
    body: JSON.stringify({ url, sortOrder, mediaType }),
  });
}

export function uploadProductMedia(file) {
  const fd = new FormData();
  fd.append('file', file);
  return fetch(`${BASE}/seller/products/upload-media`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  }).then(r => r.json()).then(d => {
    if (d.error) throw new Error(d.error);
    return d; // { url, mediaType }
  });
}

export function deleteProductImage(productId, imageId) {
  return request(`/seller/products/${productId}/images/${imageId}`, { method: 'DELETE' });
}

export function submitEnquiry(data) {
  return request('/enquiries', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function getSellerEnquiries() {
  return request('/seller/enquiries');
}

export function updateEnquiryStatus(enquiryId, status) {
  return request(`/seller/enquiries/${enquiryId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
}

export function sellerRegister(data) {
  return request('/seller/register', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function sellerLogin(data) {
  return request('/seller/login', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function sellerLogout() {
  return request('/seller/logout', { method: 'POST' });
}

export function getSellerMe() {
  return request('/seller/me');
}

export function updateSellerProfile(data) {
  return request('/seller/profile', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function sellerChangePassword(data) {
  return request('/seller/change-password', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function adminResetSellerPassword(id, newPassword) {
  return request(`/admin/sellers/${id}/reset-password`, {
    method: 'PUT',
    body: JSON.stringify({ newPassword }),
  });
}

export function getSellerProducts() {
  return request('/seller/products');
}

export function createProduct(data) {
  return request('/seller/products', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function updateProduct(id, data) {
  return request(`/seller/products/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function deleteProduct(id) {
  return request(`/seller/products/${id}`, { method: 'DELETE' });
}

/**
 * BFS flood-fill from the four corners to detect and replace the background
 * colour with white.  Works well for product photos taken on a solid or
 * near-solid backdrop.  Tolerance is the max Euclidean RGB distance from the
 * sampled background colour before a pixel is considered "not background".
 */
function floodFillWhite(ctx, width, height, tolerance = 40) {
  const imageData = ctx.getImageData(0, 0, width, height);
  const d = imageData.data;

  // Sample background colour from all four corners and average
  const samples = [[0, 0], [width - 1, 0], [0, height - 1], [width - 1, height - 1]];
  let bgR = 0, bgG = 0, bgB = 0;
  samples.forEach(([x, y]) => {
    const i = (y * width + x) * 4;
    bgR += d[i]; bgG += d[i + 1]; bgB += d[i + 2];
  });
  bgR = bgR / 4; bgG = bgG / 4; bgB = bgB / 4;

  const visited = new Uint8Array(width * height);
  const stack = [];
  samples.forEach(([x, y]) => stack.push(y * width + x));

  while (stack.length) {
    const pos = stack.pop();
    if (visited[pos]) continue;
    visited[pos] = 1;

    const i = pos * 4;
    const dr = d[i] - bgR, dg = d[i + 1] - bgG, db = d[i + 2] - bgB;
    if (Math.sqrt(dr * dr + dg * dg + db * db) > tolerance) continue;

    // Replace pixel with white
    d[i] = 255; d[i + 1] = 255; d[i + 2] = 255; d[i + 3] = 255;

    const x = pos % width, y = Math.floor(pos / width);
    if (x > 0)          stack.push(pos - 1);
    if (x < width - 1)  stack.push(pos + 1);
    if (y > 0)          stack.push(pos - width);
    if (y < height - 1) stack.push(pos + width);
  }

  ctx.putImageData(imageData, 0, 0);
}

/**
 * Resize image to a square canvas, flood-fill the background with white,
 * and export as JPEG.  Fast, no external model download required.
 */
export function compressImage(file, size = 800, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);

      // Draw original image onto a same-size working canvas first
      const work = document.createElement('canvas');
      work.width = img.width;
      work.height = img.height;
      const wCtx = work.getContext('2d');
      wCtx.drawImage(img, 0, 0);

      // Remove background colour (flood-fill from corners → white)
      floodFillWhite(wCtx, img.width, img.height);

      // Scale result onto square output canvas, centred, white background
      const out = document.createElement('canvas');
      out.width = size;
      out.height = size;
      const oCtx = out.getContext('2d');
      oCtx.fillStyle = '#ffffff';
      oCtx.fillRect(0, 0, size, size);
      const scale = Math.min(size / img.width, size / img.height);
      const drawW = Math.round(img.width * scale);
      const drawH = Math.round(img.height * scale);
      oCtx.drawImage(work, Math.round((size - drawW) / 2), Math.round((size - drawH) / 2), drawW, drawH);

      out.toBlob(
        (blob) => {
          if (!blob) { reject(new Error('Image processing failed')); return; }
          resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
            type: 'image/jpeg',
            lastModified: Date.now(),
          }));
        },
        'image/jpeg',
        quality,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load image')); };
    img.src = url;
  });
}

export function uploadProductImage(file) {
  const fd = new FormData();
  fd.append('file', file);
  return fetch(`${BASE}/seller/products/upload-image`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  }).then(r => r.json()).then(d => {
    if (d.error) throw new Error(d.error);
    return d;
  });
}

export function getSellerOrders() {
  return request('/seller/orders');
}

export function getSellerStats() {
  return request('/seller/stats');
}

export function updateOrderStatus(orderId, status) {
  return request(`/seller/orders/${orderId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
}

export function adminGetSellers(status) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return request(`/admin/sellers${qs}`);
}

export function adminUpdateSellerStatus(id, status) {
  return request(`/admin/sellers/${id}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
}

export function adminToggleSellerToucan(id, enabled) {
  return request(`/admin/sellers/${id}/toucan-payment`, {
    method: 'PUT',
    body: JSON.stringify({ enabled }),
  });
}

export function getCheckoutPaymentOptions(productIds) {
  if (!productIds || productIds.length === 0) return Promise.resolve({ toucanAvailable: false });
  return request(`/products/payment-options?pids=${productIds.join(',')}`);
}

export function adminGetOrders() {
  return request('/admin/orders');
}

export function adminGetStats() {
  return request('/admin/stats');
}

export function adminGetDashboard() {
  return request('/admin/dashboard');
}

export function getStockForecast() {
  return request('/seller/stock-forecast');
}

export function getStockAlerts() {
  return request('/seller/stock-alerts');
}

export function setStockAlert(productId, alertThreshold) {
  return request(`/seller/stock-alerts/${productId}`, {
    method: 'PUT',
    body: JSON.stringify({ alertThreshold }),
  });
}

export function deleteStockAlert(productId) {
  return request(`/seller/stock-alerts/${productId}`, { method: 'DELETE' });
}

export function adminGetStockForecast(sellerId) {
  const qs = sellerId ? `?sellerId=${sellerId}` : '';
  return request(`/admin/stock-forecast${qs}`);
}

export function adminSetStockAlert(productId, alertThreshold) {
  return request(`/admin/stock-alerts/${productId}`, {
    method: 'PUT',
    body: JSON.stringify({ alertThreshold }),
  });
}

export function adminDeleteStockAlert(productId) {
  return request(`/admin/stock-alerts/${productId}`, { method: 'DELETE' });
}

export function adminGetCategories() {
  return request('/admin/categories');
}

export function adminCreateCategory(data) {
  return request('/admin/categories', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function adminUpdateCategory(id, data) {
  return request(`/admin/categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function adminDeleteCategory(id) {
  return request(`/admin/categories/${id}`, { method: 'DELETE' });
}

// ── Order Receipt ─────────────────────────────────────────────────────────────

const RECEIPT_STYLES = `
  <link href="https://fonts.googleapis.com/css2?family=Roboto+Mono&display=swap" rel="stylesheet">
  <style>
    @page { size: 58mm auto; margin: 0; }
    * { box-sizing: border-box; }
    body {
      font-family: 'Roboto Mono', monospace;
      font-size: 12px;
      line-height: 1.4;
      width: 58mm;
      margin: 0 auto;
      padding: 6px 4px;
      color: #000;
    }
    .center { text-align: center; }
    .right  { text-align: right; }
    hr { border: none; border-top: 1px dashed #000; margin: 5px 0; }
    .shop-name  { font-size: 15px; font-weight: 700; text-transform: uppercase; margin: 0 0 2px; }
    .shop-sub   { font-size: 10px; margin: 1px 0; }
    .section-hdr{ font-size: 10px; font-weight: 700; text-transform: uppercase;
                  background: #000; color: #fff; padding: 1px 3px; margin: 4px 0 2px; }
    .info-row   { font-size: 11px; display: flex; justify-content: space-between; margin: 1px 0; }
    .info-label { color: #555; }
    table.items { width: 100%; border-collapse: collapse; margin: 3px 0; }
    table.items th { font-size: 10px; border-bottom: 1px dashed #000; padding: 1px 0; }
    table.items td { font-size: 11px; padding: 2px 0; vertical-align: top; }
    table.items td.n { width: 44%; word-break: break-word; }
    table.items td.q { width: 10%; text-align: center; }
    table.items td.p { width: 22%; text-align: right; }
    table.items td.t { width: 24%; text-align: right; font-weight: 600; }
    .sub-row    { display: flex; justify-content: flex-end; font-size: 10px; color: #555; padding: 1px 0; }
    .totals     { width: 100%; border-collapse: collapse; margin-top: 3px; }
    .totals td  { font-size: 11px; padding: 1px 0; }
    .totals td.l{ text-align: left; color: #555; }
    .totals td.r{ text-align: right; }
    .grand-total td { font-size: 14px; font-weight: 700; border-top: 2px solid #000; padding-top: 3px; }
    .footer     { margin-top: 8px; font-size: 11px; text-align: center; font-weight: 700; }
    @media screen {
      body { border: 1px dashed #ccc; max-width: 230px; }
    }
  </style>`;

export function buildReceiptHtml(order, orderRef) {
  const items       = order.items || [];
  const orderCurrency = (items[0]?.currency) || order.currency || 'INR';
  const total   = parseFloat(order.total_amount || order.total || order.totalAmount || 0);
  const courier = parseFloat(order.courier_amount || 0);
  const name    = order.buyer_name  || order.buyerName  || '';
  const phone   = order.buyer_phone || order.phone      || '';
  const address = order.delivery_address || order.deliveryAddress || '';
  const city    = order.city || '';
  const fMode   = (order.fulfillment_mode || 'DELIVERY').toUpperCase();
  const fLabel  = fMode === 'PICKUP' ? 'Pickup' : fMode === 'PARCEL' ? 'Parcel / To-Go' : 'Delivery';
  const rawDate = order.created_at || order.createdAt;
  const date    = rawDate
    ? new Date(rawDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  // Group items by seller
  const sellerMap = new Map();
  for (const item of items) {
    const sid  = String(item.seller_id || item.sellerId || 'unknown');
    const sName = item.seller_name || item.sellerName || 'Seller';
    const sPhone = item.seller_phone || item.seller_whatsapp || '';
    const sCity  = item.seller_city || '';
    if (!sellerMap.has(sid)) sellerMap.set(sid, { name: sName, phone: sPhone, city: sCity, items: [] });
    sellerMap.get(sid).items.push(item);
  }
  const sellerGroups = Array.from(sellerMap.values());
  const multiSeller  = sellerGroups.length > 1;

  // Build header — single seller: show their details; multi-seller: show "ORDER RECEIPT"
  const headerHtml = multiSeller
    ? `<div class="center"><div class="shop-name">Order Receipt</div></div>`
    : (() => {
        const s = sellerGroups[0] || {};
        return `<div class="center">
          <div class="shop-name">${s.name}</div>
          ${s.city  ? `<div class="shop-sub">${s.city}</div>` : ''}
          ${s.phone ? `<div class="shop-sub">Ph: ${s.phone}</div>` : ''}
        </div>`;
      })();

  // Build item sections
  const itemSectionsHtml = sellerGroups.map(s => {
    const rows = s.items.map(item => {
      const pname = item.product_name || item.productName || item.name || 'Item';
      const cur   = item.currency || orderCurrency;
      const sub   = (item.price || 0) * (item.quantity || 1);
      return `<tr>
        <td class="n">${pname}</td>
        <td class="q">${item.quantity}</td>
        <td class="p">${formatPrice(item.price || 0, cur)}</td>
        <td class="t">${formatPrice(sub, cur)}</td>
      </tr>`;
    }).join('');
    const subTotal = s.items.reduce((sum, i) => sum + (i.price || 0) * (i.quantity || 1), 0);
    const cur0 = s.items[0]?.currency || orderCurrency;
    return `
      ${multiSeller ? `<div class="section-hdr">${s.name}${s.city ? ' · ' + s.city : ''}${s.phone ? ' · ' + s.phone : ''}</div>` : ''}
      <table class="items">
        <thead><tr>
          <th class="n" style="text-align:left">Item</th>
          <th class="q">Qty</th>
          <th class="p" style="text-align:right">Price</th>
          <th class="t" style="text-align:right">Total</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      ${multiSeller ? `<div class="sub-row">Subtotal: ${formatPrice(subTotal, cur0)}</div>` : ''}`;
  }).join('<hr>');

  return `<!DOCTYPE html><html><head><meta charset="UTF-8">
  <title>Receipt ${orderRef}</title>${RECEIPT_STYLES}
  </head><body>
  ${headerHtml}
  <hr>
  <div class="info-row"><span class="info-label">Order</span><span>${orderRef}</span></div>
  <div class="info-row"><span class="info-label">Date</span><span>${date}</span></div>
  <div class="info-row"><span class="info-label">Name</span><span>${name}</span></div>
  <div class="info-row"><span class="info-label">Phone</span><span>${phone}</span></div>
  ${fMode === 'DELIVERY' && address ? `<div class="info-row"><span class="info-label">Address</span><span>${address}${city ? ', ' + city : ''}</span></div>` : ''}
  <div class="info-row"><span class="info-label">Mode</span><span>${fLabel}</span></div>
  <div class="info-row"><span class="info-label">Payment</span><span>Cash on Delivery</span></div>
  <hr>
  ${itemSectionsHtml}
  <hr>
  <table class="totals">
    ${courier > 0 ? `
    <tr><td class="l">Products</td><td class="r">${formatPrice(total - courier, orderCurrency)}</td></tr>
    <tr><td class="l">Courier</td><td class="r">${formatPrice(courier, orderCurrency)}</td></tr>` : ''}
    <tr class="grand-total"><td class="l">TOTAL</td><td class="r">${formatPrice(total, orderCurrency)}</td></tr>
  </table>
  <hr>
  <div class="footer">Thank you! Visit again!!!</div>
  </body></html>`;
}

export function printOrderReceipt(order, orderRef) {
  const thtml = buildReceiptHtml(order, orderRef);

  // Android WebView Bluetooth thermal printer bridge (same pattern as cashsale.jsp)
  if (typeof window.AndroidPrint !== 'undefined') {
    const items = (order.items || []).map(item => ({
      name:  item.product_name || item.productName || item.name || '',
      qty:   String(item.quantity || 1),
      price: String(parseFloat(item.price || 0).toFixed(2)),
      total: String(((item.price || 0) * (item.quantity || 1)).toFixed(2)),
    }));
    const sellerIds2 = [...new Set((order.items||[]).map(i=>i.seller_id||i.sellerId).filter(Boolean))];
    const androidTitle = sellerIds2.length === 1
      ? ((order.items||[])[0]?.seller_name || 'Order Receipt') : 'Order Receipt';
    const receiptData = JSON.stringify({
      businessTitle: androidTitle,
      invoiceNo:     orderRef,
      date:          order.created_at || order.createdAt || new Date().toISOString(),
      customer:      order.buyer_name || order.buyerName || '',
      paymentType:   'Cash on Delivery',
      refNo:         orderRef,
      total:         parseFloat(order.total_amount || order.total || order.totalAmount || 0).toFixed(2),
      footerText:    'Thank you! Visit again!!!',
      items,
    });
    window.AndroidPrint.print(thtml, receiptData);
    return;
  }

  // Browser fallback — open popup and print
  const win = window.open('', '_blank', 'width=300,height=600');
  win.document.write(thtml);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); setTimeout(() => win.close(), 3000); }, 400);
}

export function sendOrderReceipt(to, order, orderRef) {
  const items   = order.items || [];
  const sellerIds = [...new Set(items.map(i => i.seller_id || i.sellerId).filter(Boolean))];
  const sellerName = sellerIds.length === 1
    ? (items[0]?.seller_name || items[0]?.sellerName || 'SmaBizZ Marketplace')
    : 'SmaBizZ Marketplace';
  const emailCurrency = (items[0] && (items[0].currency || 'INR')) || order.currency || 'INR';
  const total   = parseFloat(order.total_amount || order.total || order.totalAmount || 0);
  const courier = parseFloat(order.courier_amount || 0);
  const name    = order.buyer_name || order.buyerName || '';
  const phone   = order.buyer_phone || order.phone || '';
  const address = order.delivery_address || order.deliveryAddress || '';
  const city    = order.city || '';
  const fMode   = (order.fulfillment_mode || 'DELIVERY').toUpperCase();

  const rows = items.map(item => {
    const pname = item.product_name || item.productName || item.name || `Product #${item.product_id || item.productId}`;
    const cur   = item.currency || emailCurrency;
    const sub   = (item.price || 0) * (item.quantity || 1);
    return `<tr><td>${pname}</td><td style="text-align:center">${item.quantity}</td><td style="text-align:right">${formatPrice(item.price||0, cur)}</td><td style="text-align:right">${formatPrice(sub, cur)}</td></tr>`;
  }).join('');

  const body = `
    <p>Dear <strong>${name}</strong>,</p>
    <p>Thank you for your order from <strong>${sellerName}</strong>! Here is your order receipt.</p>
    <table><tr><th colspan="2" style="background:#0066CC;color:#fff">Order: ${orderRef} — ${sellerName}</th></tr>
    <tr><td style="color:#666">Name</td><td>${name}</td></tr>
    <tr><td style="color:#666">Phone</td><td>${phone}</td></tr>
    <tr><td style="color:#666">Fulfillment</td><td>${fMode === 'PICKUP' ? 'Pickup' : fMode === 'PARCEL' ? 'Parcel/To-Go' : 'Delivery'}</td></tr>
    ${address && fMode === 'DELIVERY' ? `<tr><td style="color:#666">Delivery Address</td><td>${address}</td></tr>` : ''}
    ${city && fMode === 'DELIVERY' ? `<tr><td style="color:#666">City</td><td>${city}</td></tr>` : ''}
    </table><br>
    <table><thead><tr style="background:#f2f2f2"><th>Product</th><th>Qty</th><th>Price</th><th>Total</th></tr></thead>
    <tbody>${rows}</tbody>
    ${courier > 0 ? `<tr><td colspan="3" style="color:#666">Courier</td><td>${formatPrice(courier, emailCurrency)}</td></tr>` : ''}
    <tfoot><tr><td colspan="3"><strong>Grand Total</strong></td><td><strong>${formatPrice(total, emailCurrency)}</strong></td></tr></tfoot>
    </table><br>
    <p>💳 <strong>Payment: Cash on Delivery</strong> — pay when your order arrives.</p>
    <p>${sellerName} will confirm and ${fMode === 'DELIVERY' ? 'deliver' : 'prepare'} your order shortly.</p>
    <p>Track your order: <a href="https://web2.smabizz.com/app/?page=market&view=track">Click here to track</a></p>`;

  const fd = new URLSearchParams();
  fd.append('to', to);
  fd.append('subject', `Order Confirmed — ${orderRef} | SmaBizZ Marketplace`);
  fd.append('body', body);

  return fetch('/spring/sendemail', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: fd,
  }).then(r => { if (!r.ok) throw new Error('Failed to send email'); return r.text(); });
}
